import {
  PinwheelExperienceInput,
  VaDisclaimer,
} from '@varo/graphql-schema-types';
import backgroundTextureLight from 'assets/images/vxl/component/productIntroScreen/background-texture-light.png';
import {
  NativeStackHeaderProps,
  RelativePathString,
  Stack,
  useRouter,
} from 'expo-router';
import useGetAdvanceScreenContent from 'features/advance/hooks/useGetAdvanceScreenContent';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import { useDirectDepositLoadingComponent } from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

interface DisclaimerProps {
  disclaimer: VaDisclaimer;
}

const Disclaimer = ({ disclaimer }: DisclaimerProps) => {
  const router = useRouter();
  const handleTermsAndConditionAction = (url: RelativePathString) => {
    router.push(url);
    return;
  };

  return (
    <>
      <BodyXSText numberOfLines={0} style={styles.topNote}>
        {disclaimer.aboveContent}
        <HyperlinkXsText
          onPress={() =>
            handleTermsAndConditionAction(
              disclaimer.link.url as RelativePathString,
            )
          }
        >
          {disclaimer.link.text}
        </HyperlinkXsText>
        {disclaimer.belowContent}
      </BodyXSText>
      <BodyXSText numberOfLines={0} style={styles.bottomNote}>
        {disclaimer.additionalContent}
      </BodyXSText>
    </>
  );
};

const LcmDirectDeposit = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'lcmDirectDeposit',
  });

  const {
    result: advanceScreenContent,
    errorModel: advanceScreenContentErrorModel,
    loading: advanceScreenContentLoading,
  } = useGetAdvanceScreenContent();
  const lcmScreenContent = advanceScreenContent?.marketingScreenContent;
  const contentBullets = lcmScreenContent?.bullets || [];

  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();

  const {
    shouldRedirectToDDManagementPage,
    shouldLaunchPinwheelSDK,
    isLoading: pinwheelFlowLoading,
    errorModel,
    refetchDdManagementFlow,
    handleDdManagementLinkTokenFailure,
  } = useDirectDepositManagementFlow();

  const { skipIntroScreen } = useDirectDepositLoadingComponent();

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  useBackHandler(goHome);

  const handleDirectDeposit = () => {
    if (shouldRedirectToDDManagementPage) {
      return router.push(
        '/move-money/direct-deposit/direct-deposit-management',
      );
    }

    const outcomeCallback = shouldLaunchPinwheelSDK
      ? refetchDdManagementFlow
      : goHome;

    initiatePinwheel({
      input: {
        experience: PinwheelExperienceInput.DirectDepositSwitch,
        skipIntroScreen,
      },
      launchBehavior: skipIntroScreen ? { delayAfterTokenMs: 3000 } : undefined,
      onOutcome: outcomeCallback,
      ...(shouldLaunchPinwheelSDK && {
        onLinkTokenError: handleDdManagementLinkTokenFailure,
      }),
    });
  };

  const errorScreen = errorModel || advanceScreenContentErrorModel;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} transparent primaryOnPress={goHome} />
          ),
        }}
      />

      <ScreenTransitionOverlay
        isLoading={
          isPinwheelLoading ||
          pinwheelFlowLoading ||
          advanceScreenContentLoading
        }
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={advanceScreenContentLoading}
            isPinwheelLoading={isPinwheelLoading || pinwheelFlowLoading}
          />
        }
        ErrorComponent={errorScreen ? <ErrorScreen {...errorScreen} /> : null}
      />
      <ProductIntroScreen
        headlineText={lcmScreenContent?.title}
        headlineTextStyle="expressive"
        visual="ADVANCE_A"
        topPadding={80}
        backgroundTexture={backgroundTextureLight}
        bodyContent={
          <View style={styles.bodyContainer}>
            <ContentList contentList={contentBullets} />
            {lcmScreenContent?.disclaimer ? (
              <Disclaimer disclaimer={lcmScreenContent.disclaimer} />
            ) : null}
          </View>
        }
        primaryButton={{
          label: translatedText(
            shouldRedirectToDDManagementPage
              ? 'button.manageDD'
              : 'button.setUpDD',
          ),
          onPress: handleDirectDeposit,
          analyticsId: 'directDepositFlowInitiated',
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bodyContainer: {
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  bottomNote: {
    color: theme.color.text.secondary,
    paddingBottom: theme.spacing.m,
  },
  topNote: {
    color: theme.color.text.secondary,
  },
});

export default LcmDirectDeposit;
